import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Auth.css';
import { 
  signInWithGoogle, 
  sendPasswordlessLink,
  completePasswordlessSignIn
} from '../firebase';
import sonoranLandscape from '../assets/images/dither-yosemite.jpg';
import GoogleIcon from '../components/ui/GoogleIcon';
import { storePendingProductUrl } from '../utils/consultUtils';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isCompletingAuth, setIsCompletingAuth] = useState(false);

  const getPostLoginPath = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('returnTo') || '/profile';
  };

  const persistConsultRedirect = () => {
    const urlParams = new URLSearchParams(location.search);
    const redirect = urlParams.get('redirect');
    if (redirect) {
      storePendingProductUrl(decodeURIComponent(redirect));
    }
  };

  // Handle passwordless sign-in completion
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isPasswordlessAuth = urlParams.get('auth') === 'passwordless';
    const hasAuthParams = window.location.href.includes('apiKey') && window.location.href.includes('oobCode');

    if (isPasswordlessAuth && hasAuthParams) {
      setIsCompletingAuth(true);
      const completeSignIn = async () => {
        const result = await completePasswordlessSignIn();
        if (result.success) {
          persistConsultRedirect();
          navigate(getPostLoginPath());
        } else if (result.needsEmail) {
          navigate('/confirm-email', { state: { emailLink: window.location.href } });
        } else if (result.error) {
          setError('There was an issue completing your sign-in. Please try again.');
          setIsCompletingAuth(false);
        }
      };
      completeSignIn();
    }
  }, [location, navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await signInWithGoogle();
      persistConsultRedirect();
      navigate(getPostLoginPath());
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      setError('Failed to sign in with Google. Please try again.');
    }
    setLoading(false);
  };

  const handleEmailSignIn = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    const result = await sendPasswordlessLink(email);
    if (result.success) {
      // Redirect to waiting page
      navigate('/passwordless-waiting', { state: { email, isSignup: false } });
    } else {
      setError('Failed to send sign-in link. Please check the email and try again.');
    }
    setLoading(false);
  };

  if (isCompletingAuth) {
    return (
      <div className="auth-container passwordless-waiting-container">
        <div className="auth-form-wrapper passwordless-waiting-wrapper">
          <div className="auth-waiting-content">
            <div className="auth-spinner"></div>
            <h2 className="auth-title">COMPLETING SIGN-IN</h2>
            <p className="auth-waiting-instruction">Verifying your email...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form-wrapper">
        <h2 className="auth-title">WELCOME BACK</h2>
        {message && <p className="auth-message success">{message}</p>}
        {error && <p className="auth-message error">{error}</p>}
        <form onSubmit={handleEmailSignIn} className="auth-form">
          <div className="auth-input-group">
            <label htmlFor="email">EMAIL ADDRESS</label>
            <input 
              type="email" 
              id="email" 
              name="name" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required 
              disabled={loading}
              placeholder="Enter your email address"
            />
          </div>
          <button type="submit" className="auth-button primary" disabled={loading}>
            {loading ? 'SENDING LINK...' : 'SEND SIGN-IN LINK'}
          </button>
        </form>
        <button onClick={handleGoogleSignIn} className="auth-button google" disabled={loading}>
          <GoogleIcon />
          <span>{loading ? 'SIGNING IN...' : 'CONTINUE WITH GOOGLE'}</span>
        </button>
        <p className="auth-redirect-link">
          NEED AN ACCOUNT? <a href="/signup">SIGN UP HERE.</a>
        </p>
      </div>
      <div className="auth-image-wrapper">
        <img src={sonoranLandscape} alt="Desert landscape" />
      </div>
    </div>
  );
};

export default LoginPage; 